// src/components/pages/Pessoa/modules/usePessoaEmailModule.js

import { useCallback, useEffect, useMemo, useState } from 'react';
import * as PessoaAPI from '../../../../api/pessoa';
import { ValidaEmail } from '../../../../utils/validacoes';

function createEmailDraft(pessoaId = null) {
    return {
        pessoaId: pessoaId || null,
        uuid: '',
        tipo: 'Outros',
        email: '',
        principal: false,
        ativo: true,
        funcao: 'Adicionar',
    };
}

export function usePessoaEmailModule({ pessoaId, rows = [], setRows, setFlashMessage }) {
    const [draft, setDraft] = useState(createEmailDraft(pessoaId));
    const [editingIndex, setEditingIndex] = useState(null);

    useEffect(() => {
        setDraft((prev) => ({
            ...prev,
            pessoaId: pessoaId || null,
        }));
    }, [pessoaId]);

    const resetDraft = useCallback(() => {
        setDraft(createEmailDraft(pessoaId));
        setEditingIndex(null);
    }, [pessoaId]);

    const handleEdit = useCallback(
        (row, index) => {
            setDraft({
                index,
                pessoaId: row.pessoaId || pessoaId || null,
                uuid: row.uuid || '',
                tipo: row.tipo || 'Outros',
                email: row.email || '',
                principal: !!row.principal,
                ativo: !!row.ativo,
                funcao: 'Editar',
            });
            setEditingIndex(index);
        },
        [pessoaId]
    );

    const handleDelete = useCallback(
        async (row, index) => {
            const payload = {
                index,
                pessoaId: row.pessoaId || pessoaId || null,
                uuid: row.uuid || '',
                tipo: row.tipo || 'Outros',
                email: row.email || '',
                principal: !!row.principal,
                ativo: !!row.ativo,
                funcao: 'deletar',
            };

            if (!pessoaId) {
                setRows((prev) => prev.filter((_, i) => i !== index));
                setFlashMessage?.('Email removido da lista.', 'success');
                if (editingIndex === index) resetDraft();
                return;
            }

            try {
                const result = await PessoaAPI.emails(payload);

                if (result?.data?.type === 'success') {
                    setRows(result.data.resultado || []);
                }

                setFlashMessage?.(result?.data?.message || 'Operação realizada.', result?.data?.type || 'success');

                if (editingIndex === index) resetDraft();
            } catch (error) {
                console.error(error);
                setFlashMessage?.(error?.response?.data?.message || 'Erro ao excluir email.', 'error');
            }
        },
        [pessoaId, setRows, setFlashMessage, editingIndex, resetDraft]
    );

    const handleSave = useCallback(async () => {
        if (!draft.email?.trim()) {
            setFlashMessage?.('Digite o email.', 'warning');
            return;
        }

        if (!ValidaEmail(draft.email)) {
            setFlashMessage?.('Email inválido! Verifique se o email está correto.', 'error');
            return;
        }

        const payload = {
            ...draft,
            pessoaId: pessoaId || draft.pessoaId || null,
        };

        if (!pessoaId) {
            if (String(payload.funcao).toLowerCase() === 'editar' && editingIndex !== null) {
                setRows((prev) =>
                    prev.map((item, index) =>
                        index === editingIndex
                            ? {
                                  ...item,
                                  ...payload,
                              }
                            : item
                    )
                );
                setFlashMessage?.('Email atualizado na lista.', 'success');
            } else {
                setRows((prev) => [
                    ...prev,
                    {
                        ...payload,
                        uuid: payload.uuid || `draft-email-${Date.now()}`,
                    },
                ]);
                setFlashMessage?.('Email adicionado na lista.', 'success');
            }

            resetDraft();
            return;
        }

        try {
            const result = await PessoaAPI.emails(payload);

            if (result?.data?.type === 'success') {
                setRows(result.data.resultado || []);
            }

            setFlashMessage?.(result?.data?.message || 'Operação realizada.', result?.data?.type || 'success');
            resetDraft();
        } catch (error) {
            console.error(error);
            setFlashMessage?.(error?.response?.data?.message || 'Erro ao salvar email.', 'error');
        }
    }, [draft, pessoaId, editingIndex, resetDraft, setRows, setFlashMessage]);

    const columns = useMemo(
        () => [
            {
                accessorKey: 'email',
                header: 'Email',
                size: 280,
            },
            {
                accessorKey: 'tipo',
                header: 'Tipo',
                size: 120,
            },
            {
                accessorKey: 'principal',
                header: 'Principal',
                size: 90,
                Cell: ({ row }) => (row.original.principal ? 'Sim' : 'Não'),
            },
            {
                accessorKey: 'ativo',
                header: 'Ativo',
                size: 80,
                Cell: ({ row }) => (row.original.ativo ? 'Sim' : 'Não'),
            },
        ],
        []
    );

    return {
        rows,
        draft,
        setDraft,
        columns,
        editingIndex,
        handleEdit,
        handleDelete,
        handleSave,
        resetDraft,
    };
}
